import {
  Button,
  Checkbox,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HorizontalEventCard,
  Input,
  Label,
  Select,
  DatePicker
} from '@goodneighbor/ui';
import styles from './volunteerInformation.module.scss';
import { useForm } from 'react-hook-form';
import { RegistrationFormData } from '@/components/modals/RegistrationModal/RegistrationModal';
import { Flag01 } from '@goodneighbor/ui/icons/untitled';
import { Event } from '@goodneighbor/api/schemas/events/event.schema';
import { parseForEventCardProps } from '@/services/event';
import { DEFAULT_LABELS, RegistrationFormLabels } from './constants';
import { toCalendarDateValue, toDateString } from '@/services/dateTime';
import { cx } from 'class-variance-authority';

export default function VolunteerInformation({
  form,
  event,
  organizationName,
  readonly,
  onChange,
  disabledValues,
  isSignedIn,
  onLogInClick,
  labels = DEFAULT_LABELS
}: {
  form: ReturnType<typeof useForm<RegistrationFormData>>;
  event: Event;
  organizationName: string;
  readonly?: boolean;
  onChange?: () => void;
  disabledValues?: Partial<Record<keyof RegistrationFormData, boolean>>;
  isSignedIn?: boolean;
  onLogInClick?: () => void;
  labels?: RegistrationFormLabels;
}) {
  return (
    <>
      <div className={styles.eventCardWrapper}>
        <HorizontalEventCard
          {...parseForEventCardProps(event, organizationName)}
        />
      </div>
      <div className={styles.volunteerHeader}>
        <h2 className={styles.volunteerTitle}>
          {readonly ? 'Review your registration' : 'Volunteer Information'}
        </h2>
        <div>
          {readonly ? (
            <Button color="link-color" size="xs" onClick={() => onChange?.()}>
              Change
            </Button>
          ) : (
            !isSignedIn && (
              <>
                <span className={styles.signIn}>Have an account? </span>
                <Button onClick={onLogInClick} color="link-color" size="xs">
                  Sign in
                </Button>
              </>
            )
          )}
        </div>
      </div>
      <div className={styles.form}>
        <div className={styles.row}>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    isInvalid={!!error}
                    isDisabled={readonly || disabledValues?.firstName}
                    isRequired
                    value={field.value ?? ''}
                    onChange={(value) => field.onChange(value)}
                    id="firstName"
                    label={labels.firstName}
                    placeholder="First Name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    isInvalid={!!error}
                    isDisabled={readonly || disabledValues?.lastName}
                    isRequired
                    value={field.value ?? ''}
                    onChange={(value) => field.onChange(value)}
                    id="lastName"
                    label={labels.lastName}
                    placeholder="Last Name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className={styles.row}>
          <FormField
            control={form.control}
            name="phone"
            render={({ field, fieldState: { error } }) => (
              <FormItem className={styles.formItem}>
                <FormControl>
                  <Input
                    {...field}
                    isInvalid={!!error}
                    isDisabled={readonly || disabledValues?.phone}
                    isRequired
                    value={field.value ?? ''}
                    onChange={(value) => field.onChange(value)}
                    icon={Flag01}
                    id="phone"
                    label={labels.phone}
                    format="+1 (###) ###-####"
                    placeholder="+1 (555) 123-4567"
                  />
                </FormControl>
                <FormMessage className={styles.formMessage} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    isInvalid={!!error}
                    isDisabled={readonly || disabledValues?.email}
                    isRequired
                    value={field.value ?? ''}
                    onChange={(value) => field.onChange(value)}
                    id="email"
                    label={labels.email}
                    hideIcon
                    placeholder="name@example.com"
                    type="email"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className={styles.row}>
          <FormField
            control={form.control}
            name="birthDate"
            render={({ field }) => (
              <FormItem className={styles.formItem}>
                <FormControl>
                  <div>
                    <Label
                      className={styles.inputLabel}
                      isRequired
                      id="birthDate"
                    >
                      {labels.birthDate}
                    </Label>
                    <DatePicker
                      {...field}
                      className={cx(
                        styles.datePicker,
                        !field.value && styles.placeholder
                      )}
                      aria-labelledby="birthDate"
                      presetButtonLabel="Select date"
                      onChange={(value) => {
                        const dateStr = toDateString(value);
                        field.onChange(
                          dateStr ? new Date(dateStr).toISOString() : null
                        );
                      }}
                      showActions={false}
                      shouldCloseOnSelect
                      value={toCalendarDateValue(field.value ?? null)}
                      isDisabled={readonly || disabledValues?.birthDate}
                    />
                  </div>
                </FormControl>
                <FormMessage className={styles.formMessage} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    isDisabled={readonly || disabledValues?.gender}
                    value={field.value ?? ''}
                    onChange={(value) => {
                      field.onChange(value as string);
                      form.clearErrors('gender');
                    }}
                    id="gender"
                    label={labels.gender}
                    isRequired
                    placeholder="Select gender"
                    items={[
                      {
                        label: 'Male',
                        id: 'male'
                      },
                      {
                        label: 'Female',
                        id: 'female'
                      }
                    ]}
                  >
                    {(item) => (
                      <Select.Item key={item.id} id={item.id}>
                        {item.label}
                      </Select.Item>
                    )}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div>
          <FormField
            control={form.control}
            name="isInternalVolunteer"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    isDisabled={readonly}
                    value={
                      typeof field.value === 'boolean'
                        ? field.value
                          ? 'yes'
                          : 'no'
                        : null
                    }
                    onChange={(value) => {
                      field.onChange((value as string) === 'yes');
                      form.clearErrors('isInternalVolunteer');
                    }}
                    id="isInternalVolunteer"
                    label={labels.isInternalVolunteer}
                    // TODO: make this required post v1
                    placeholder="Select an option"
                    items={[
                      {
                        label: labels.isInternalVolunteerYes,
                        id: 'yes'
                      },
                      {
                        label: labels.isInternalVolunteerNo,
                        id: 'no'
                      }
                    ]}
                  >
                    {(item) => (
                      <Select.Item key={item.id} id={item.id}>
                        {item.label}
                      </Select.Item>
                    )}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className={styles.checkboxes}>
        <FormField
          control={form.control}
          name="acceptedPhoneTermsAt"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Checkbox
                  isDisabled={readonly}
                  isSelected={!!field.value}
                  onChange={(value) => {
                    field.onChange(value ? new Date().toISOString() : '');
                    form.clearErrors('acceptedPhoneTermsAt');
                  }}
                  id="acceptedPhoneTermsAt"
                  label={labels.acceptedPhoneTermsAt}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="acceptedCancellationPolicyAt"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Checkbox
                  isDisabled={readonly}
                  isSelected={!!field.value}
                  onChange={(value) => {
                    field.onChange(value ? new Date().toISOString() : '');
                    form.clearErrors('acceptedCancellationPolicyAt');
                  }}
                  id="acceptCancellationPolicy"
                  label={labels.acceptedCancellationPolicyAt}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="prefersSpanishCommunication"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Checkbox
                  isDisabled={readonly}
                  isSelected={field.value}
                  onChange={field.onChange}
                  id="prefersSpanishCommunication"
                  label={labels.prefersSpanishCommunication}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
